import * as React from "react";
import * as Ons from "react-onsenui";
import { deepEqual } from "../deep-equal";
import { DistanceRequest, DistanceResult, geoService } from "../GeoService";
import { isLoading, Loading, loading } from "../Loading";
import { Geo } from "../messages";
import { STRINGS } from "../strings";
import { translate } from "../translate";

export function calcGeoDistance(location1: Geo, location2: Geo, unit: string) {
  // Multiply distance to make it more close to real life
  const DISTANCE_PERCENTAGE_OFFSET = 1.1;

  const radlat1 = (Math.PI * location1.lat) / 180;
  const radlat2 = (Math.PI * location2.lat) / 180;
  const theta = location1.lng - location2.lng;
  const radtheta = (Math.PI * theta) / 180;
  let dist =
    Math.sin(radlat1) * Math.sin(radlat2) +
    Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
  if (dist > 1) {
    dist = 1;
  }
  dist = Math.acos(dist);
  dist = (dist * 180) / Math.PI;
  dist = dist * 60 * 1.1515;
  if (unit === "K") {
    dist = dist * 1.609344;
  }
  if (unit === "N") {
    dist = dist * 0.8684;
  }

  return dist * DISTANCE_PERCENTAGE_OFFSET;
}

export interface DistanceLabelProps {
  distanceRequest: DistanceRequest;
  formatter: (distance: number) => number;
}

export interface DistanceLabelState {
  distance: number | null | Loading;
}

export class DistanceLabel extends React.Component<DistanceLabelProps, DistanceLabelState> {
  request: { promise: Promise<DistanceResult>; cancel: () => void } | null;

  constructor(props: DistanceLabelProps) {
    super(props);

    let distance: number | Loading;
    this.request = null;

    if (
      typeof this.props.distanceRequest.origin !== "string" &&
      typeof this.props.distanceRequest.destination !== "string"
    ) {
      distance =
        calcGeoDistance(
          this.props.distanceRequest.origin,
          this.props.distanceRequest.destination,
          "K"
        ) * 1000;
    } else {
      distance = loading();
      this.loadDistance(this.props.distanceRequest);
    }

    this.state = {
      distance: distance,
    };
  }

  shouldComponentUpdate(nextProps: DistanceLabelProps, nextState: DistanceLabelState) {
    return (
      !deepEqual(nextProps.distanceRequest, this.props.distanceRequest) ||
      !deepEqual(nextState, this.state)
    );
  }

  async loadDistance(distanceRequest: DistanceRequest) {
    this.request = makeCancelable(geoService.distance(distanceRequest)) as {
      promise: Promise<DistanceResult>;
      cancel: () => void;
    };

    try {
      const distance = await this.request.promise;
      this.setState((_prevState: DistanceLabelState) => ({
        distance: distance,
      }));
    } catch (reason) {
      if (!reason.isCanceled) {
        throw reason;
      }
    }
  }

  componentDidUpdate(nextProps: DistanceLabelProps) {
    if (!deepEqual(nextProps.distanceRequest, this.props.distanceRequest)) {
      this.loadDistance(nextProps.distanceRequest);
    }
  }

  componentWillUnmount() {
    if (this.request !== null) {
      this.request.cancel();
    }
  }

  render() {
    if (isLoading(this.state.distance)) {
      return <Ons.ProgressCircular indeterminate={true} />;
    }

    if (this.state.distance === null) {
      return <span className="DistanceLabel">{translate(STRINGS.NA)}</span>;
    }

    return <span className="DistanceLabel">{this.props.formatter(this.state.distance)}</span>;
  }
}

const makeCancelable = (promise: any) => {
  let hasCanceled = false;

  const wrappedPromise = new Promise<any>((resolve, reject) => {
    promise.then(
      (val: any) => (hasCanceled ? reject({ isCanceled: true }) : resolve(val)),
      (error: any) => (hasCanceled ? reject({ isCanceled: true }) : reject(error))
    );
  });

  return {
    promise: wrappedPromise,
    cancel: () => {
      hasCanceled = true;
    },
  };
};
